/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  type AbsoluteURL,
  allProjectFlows,
  type FormattedNotification,
  formatBidRequestNotification,
  formatDateLocal,
  formatUserStr,
  getNotificationSource,
  gisFlows,
  NotificationSeverity,
  type NotificationsQueryResult,
  type ObjectIdMap,
  type PANotification,
  type PrefectFlow,
  PricesType,
  paBatchFlows,
  paFlows,
} from '@poweralpha/common';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import { find, orderBy, upperCase, upperFirst } from 'lodash';
import { runReportPath, sweepPath } from '../project-simulate/paths';
import { serviceNameToResultURL } from '../simulation-status/routes';
import { getLogsLink, getScenarioLink } from '../simulation-status/utils';
import {
  componentCreationFlows,
  componentCreationFlowToBaseUrls,
  componentCreationFlowToDetailUrls,
  notLoadedDefault,
} from './const';
import { getNotificationDetails } from './notificationDetails';

dayjs.extend(relativeTime);

const getFallbackNotificationLink = (notification: PANotification): AbsoluteURL => {
  const bidRequest = notification.meta.bid_request;
  if (bidRequest) {
    const versionSearch: `?${string}` | '' =
      bidRequest.version !== undefined ? `?version=${bidRequest.version}` : '';
    return `/bid-requests/${bidRequest.id}/bid-matrix${versionSearch}`;
  }

  return '/notifications';
};

const getFallbackUserStr = (notification: PANotification, userEmail: string | undefined) => {
  try {
    return formatUserStr(notification.created_by, { userEmail });
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: keep bad notification payloads visible without crashing UI
    console.error('Unable to format notification user', notification, error);
    return 'Someone';
  }
};

const formatFallbackNotification = (
  notification: PANotification,
  userEmail: string | undefined,
): FormattedNotification => {
  const targetedMessage = notification.meta.bid_request?.targeted_message;
  const subject =
    notification.meta.bid_request?.name ?? notification.workflow_name ?? 'Notification';
  const fallbackMessage =
    targetedMessage ?? 'Open the notification center to review this notification.';

  return {
    link: getFallbackNotificationLink(notification),
    subject,
    notif_content: `${getFallbackUserStr(notification, userEmail)} sent a notification. ${fallbackMessage}`,
    message: notification.message ?? null,
    whenStr: dayjs(notification.created_at).fromNow(),
  };
};

const componentCreationCustomErrorResponse: Partial<
  Record<PrefectFlow, (n: PANotification) => { message: string; link: AbsoluteURL } | undefined>
> = {
  'Add Price Curves': (n) => {
    if (!n.meta.column_index) return undefined;
    return {
      message: `skipped a ${
        n.meta.prices_type === PricesType.REC
          ? upperCase(PricesType.REC)
          : upperFirst(n.meta.prices_type)
      } record due to a validation error (column index: ${n.meta.column_index})`,
      link: '/notifications',
    };
  },
};

const formatPlantSimBatchNotification = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
) => {
  const { meta } = notification;

  let link =
    notification.workflow_name === 'PlantSim v2 Batch'
      ? serviceNameToResultURL.plant_sim_v2_batch
      : serviceNameToResultURL.plant_sim_batch;
  let subject = `${notification.workflow_name} Simulate`;
  let message = `${notification.workflow_name} simulation started on ${formatDateLocal(
    meta.started,
  )}${projectNames ? ` for ${projectNames[0]}` : ''}`;
  if (notification.severity === NotificationSeverity.ERROR) {
    subject = `${subject} Failed`;
    message = `${message} failed due to an error.`;
    link = getLogsLink(notification.workflow_name, meta.project_id);
  } else if (notification.severity === NotificationSeverity.WARNING && meta.num_of_errors) {
    subject = `${subject} Successful with Warnings`;
    message = `${message} completed, with ${meta.num_of_errors} run${
      meta.num_of_errors > 1 ? 's' : ''
    } failing.`;
  } else if (notification.severity === NotificationSeverity.SUCCESS) {
    subject = `${subject} Successful`;
    message = `${message} completed successfully.`;
  } else {
    throw new Error('Not implemented');
  }

  return { message, link, subject, possessive: true };
};

const formatProjectBatchNotification = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
) => {
  const { meta } = notification;

  let link: AbsoluteURL;
  let subject = `${projectNames?.at(0)} Batch`;
  let message = `batch simulation for ${projectNames?.at(0)} started on ${formatDateLocal(
    meta.started,
  )}`;
  if (notification.severity === NotificationSeverity.ERROR) {
    subject = `${subject} Failed`;
    message = `${message} failed due to an error.`;
    link = `/projects/${meta.project_id}/logs`;
  } else if (
    notification.severity === NotificationSeverity.WARNING &&
    meta.num_of_errors &&
    meta.batch_workflow_id
  ) {
    link = sweepPath(`${meta.project_id}`, `${meta.batch_workflow_id}`);
    subject = `${subject} Successful with Warnings`;
    message = `${message} completed, with ${meta.num_of_errors} run${
      meta.num_of_errors > 1 ? 's' : ''
    } failing.`;
  } else if (notification.severity === NotificationSeverity.SUCCESS) {
    subject = `${subject} Successful`;
    link = sweepPath(`${meta.project_id}`, `${meta.batch_workflow_id}`);
    message = `${message} completed successfully.`;
  } else {
    throw new Error('Not implemented');
  }

  return { message, link, subject: `${subject} (${notification.workflow_name})`, possessive: true };
};

const formatProjectSimNotification = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
) => {
  const { meta } = notification;

  let link: AbsoluteURL;
  let subject = `${projectNames?.at(0)} Simulation`;
  let message = `simulation for ${projectNames?.at(0)} started on ${formatDateLocal(meta.started)}`;
  if (notification.severity === NotificationSeverity.ERROR) {
    subject = `${subject} Failed`;
    message = `${message} failed due to an error.`;
    link = `/projects/${meta.project_id}/logs`;
  } else if (notification.severity === NotificationSeverity.SUCCESS && meta.scenario_id) {
    subject = `${subject} Successful`;
    link = runReportPath(`${meta.project_id}`, `${meta.scenario_id}`);
    message = `${message} completed successfully.`;
  } else {
    throw new Error('Not implemented');
  }

  return { message, link, subject: `${subject} (${notification.workflow_name})`, possessive: true };
};

const formatSingleRunNotification = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
) => {
  const { meta } = notification;

  let link: AbsoluteURL;
  let message: string;
  let subject = `${notification.workflow_name} Simulate`;
  const baseMsg = `${notification.workflow_name} simulation started on ${formatDateLocal(
    meta.started,
  )}`;

  if (notification.severity === NotificationSeverity.ERROR) {
    subject = `${subject} Failed`;
    message = `${baseMsg} failed due to an error.`;
    link = getLogsLink(notification.workflow_name, meta.project_id);
  } else if (notification.severity === NotificationSeverity.WARNING && notification.message) {
    message = `${baseMsg} encountered a warning.`;
    subject = `${subject}: Warning`;
    link = getScenarioLink(notification.workflow_name, meta.scenario_id ?? '');
  } else if (notification.severity === NotificationSeverity.SUCCESS) {
    subject = `${subject} Successful`;
    link = getScenarioLink(notification.workflow_name, meta.scenario_id ?? '');
    message = `${baseMsg} completed successfully.`;
  } else {
    throw new Error('Not implemented');
  }

  return {
    message,
    link,
    subject: `${subject}${projectNames ? ` [${projectNames[0]}]` : ''}`,
    possessive: true,
  };
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: refactor
const formatRecordCreationNotification = (notification: PANotification) => {
  const { meta } = notification;

  if (!notification.workflow_name) {
    throw new Error('Workflow name not defined');
  }

  const baseUrl = componentCreationFlowToBaseUrls[notification.workflow_name];
  const customDetailUrl = componentCreationFlowToDetailUrls[notification.workflow_name];

  let link = customDetailUrl ? customDetailUrl(notification) : baseUrl;
  let message = `flow '${notification.workflow_name}' started on ${formatDateLocal(meta.started)}`;
  if (meta.existing_id) {
    if (notification.severity === NotificationSeverity.WARNING) {
      message = `${message} skipped a duplicate record.`;
    } else if (notification.severity === NotificationSeverity.ERROR) {
      message = `${message} failed due to a duplicate record.`;
    }

    link += `${meta.existing_id}`;
  } else if (notification.message) {
    const customCallback = componentCreationCustomErrorResponse[notification.workflow_name];
    const customRes = customCallback ? customCallback(notification) : undefined;
    message = `${message} ${customRes?.message ?? 'failed due to an error'}.`;
    link = customRes?.link ?? '/db/logs';
  } else {
    const recordPlural = (meta.num_successes ?? 0) > 1 ? 's' : '';
    message = `${message} completed successfully${
      meta.num_successes ? `, with ${meta.num_successes} record${recordPlural} saved` : ''
    }.`;
  }

  return {
    message,
    link: link as AbsoluteURL,
    subject: notification.workflow_name,
    possessive: true,
  };
};

type NotifFormatFn = (
  notif: PANotification,
  projectNames: Array<string> | undefined,
) => {
  link: AbsoluteURL;
  message: string;
  subject: string;
  possessive?: boolean;
};

// Agent run notifications carry no workflow_name; they are keyed by the
// conversation they belong to instead.
const formatAgentNotification: NotifFormatFn = (notification) => ({
  link: (notification.meta.agent_conversation_id
    ? `/agent/${notification.meta.agent_conversation_id}`
    : '/agent') as AbsoluteURL,
  // Prefer a preview of the agent's answer; fall back to a generic label.
  message: notification.meta.agent_message_preview?.trim() || 'Agent run finished',
  subject: notification.meta.agent_conversation_title ?? 'Agent',
  possessive: true,
});

const getFormatter = (
  notification: PANotification,
  projectNames: Array<string> | undefined,
): NotifFormatFn | undefined => {
  const { meta } = notification;
  if (meta.bid_request && projectNames) {
    return formatBidRequestNotification; // bid request
  }

  if (meta.agent_conversation_id) {
    return formatAgentNotification; // agent run finished
  }

  if (!notification.workflow_name) {
    throw new Error('Workflow name must be defined for workflow notifications');
  }

  if (
    ['PlantSim Batch', 'PlantSim v2 Batch'].includes(notification.workflow_name) &&
    meta.started
  ) {
    return formatPlantSimBatchNotification; // plantsim batch
  }

  if (projectNames && meta.started && allProjectFlows.includes(notification.workflow_name)) {
    if (paBatchFlows.includes(notification.workflow_name)) {
      return formatProjectBatchNotification; // project batch
    }
    if (
      paFlows.includes(notification.workflow_name) ||
      gisFlows.includes(notification.workflow_name)
    ) {
      return formatProjectSimNotification; // single-run project
    }
  }

  if (componentCreationFlows.includes(notification.workflow_name)) {
    return formatRecordCreationNotification; // record creation run
  }

  if (meta.started) {
    return formatSingleRunNotification; // single service run
  }

  return undefined;
};

// Deep-links the notification-center bell item to the shared conversation. The
// agent panel is a routeless overlay, so we hand off via a query param that
// AgentPanelProvider consumes (opens the panel + strips the param).
const formatAgentShareNotification = (
  notification: PANotification,
  userEmail: string | undefined,
): FormattedNotification => {
  const share = notification.meta.agent_conversation_share;
  const owner = formatUserStr(notification.created_by, { userEmail, possessive: true });
  const titleSuffix = share?.title ? `: ${share.title}` : '';
  return {
    link: `/?agentConversation=${encodeURIComponent(share?.conversation_id ?? '')}` as AbsoluteURL,
    subject: 'Conversation shared with you',
    notif_content: `${owner} shared an agent conversation with you${titleSuffix}`,
    message: null,
    whenStr: dayjs(notification.created_at).fromNow(),
  };
};

const getProjectNames = (
  { project_id }: PANotification['meta'],
  projectIdMap: Record<string, string>,
): Array<string> | undefined => {
  if (Array.isArray(project_id)) return project_id.map((id) => projectIdMap[id]);
  return project_id ? [projectIdMap[project_id]] : undefined;
};

export const formatNotification = (
  notification: PANotification,
  projectIdMap: Record<string, string>,
  userEmail: string | undefined,
): FormattedNotification => ({
  ...formatNotificationContent(notification, projectIdMap, userEmail),
  source: getNotificationSource(notification),
  details: getNotificationDetails(notification, getProjectNames(notification.meta, projectIdMap)),
});

const formatNotificationContent = (
  notification: PANotification,
  projectIdMap: Record<string, string>,
  userEmail: string | undefined,
): FormattedNotification => {
  const { meta } = notification;

  // Share notifications carry no workflow_name, so handle them before the
  // workflow-driven formatters (which require one).
  if (meta.agent_conversation_share) {
    return formatAgentShareNotification(notification, userEmail);
  }

  const projectNames = getProjectNames(meta, projectIdMap);

  const error = notification.message ?? undefined;
  const formatter = getFormatter(notification, projectNames);

  if (!formatter) {
    // biome-ignore lint/suspicious/noConsole: console is ok here
    console.error(notification);
    throw new Error('Not implemented');
  }

  let formatted: ReturnType<NonNullable<typeof formatter>>;
  try {
    formatted = formatter(notification, projectNames);
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: keep bad notification payloads visible without crashing UI
    console.error('Unable to format notification', notification, error);
    return formatFallbackNotification(notification, userEmail);
  }

  const { link, subject, message, possessive } = formatted;

  // Agent notifications show the answer preview verbatim (no "You/Your" prefix).
  const isAgentNotification = Boolean(meta.agent_conversation_id);
  const notif_content = isAgentNotification
    ? message
    : `${formatUserStr(notification.created_by, { userEmail, possessive })} ${message}`;

  return {
    link,
    subject,
    notif_content,
    message: error ?? null,
    whenStr: dayjs(notification.created_at).fromNow(),
  };
};

export const formatNotifications = (
  res: NotificationsQueryResult,
  projectIdMap: ObjectIdMap | undefined,
  userEmail: string | undefined,
) => {
  if (!projectIdMap) {
    return notLoadedDefault;
  }

  const notifs = res.rows.map((n) => {
    const { meta, ...rest } = n;
    return {
      ...rest,
      ...formatNotification(n, projectIdMap, userEmail),
    };
  });

  const unreadNotifs = res.unread.map((n) => {
    const prevFormatted = find(notifs, ({ id }) => id === n.id);
    if (prevFormatted) return prevFormatted;
    const { meta, ...rest } = n;
    return {
      ...rest,
      ...formatNotification(n, projectIdMap, userEmail),
    };
  });

  return { ...res, unread: unreadNotifs, rows: orderBy(notifs, 'created_at', 'desc') };
};
